import React, { use, useState } from 'react';
import { Navbar, Nav, Offcanvas } from 'react-bootstrap';
import { Gear } from 'react-bootstrap-icons';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SHXIcon, SHXButton, clsx } from '@soulhx/ui-common';
import { Settings } from './Settings.js';
import { ShxHelpText } from './ShxHelpText.js';
import { useUserSetting } from '@soulhx/client-store';
import { HeaderCommsLink } from './helpers/HeaderCommsLink.js';
import { SoulHXContext, type SoulHXContextType } from '../../stores/SoulHXContext.js';

export const Header = () => {
  const { user } = use(SoulHXContext) as SoulHXContextType;
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const { t } = useTranslation();
  const { settingValue: homeHT, isLoading: homeHTLoading } = useUserSetting<boolean>('helpText.home.headerHome');
  const { settingValue: prayerHT, isLoading: prayerHTLoading } = useUserSetting<boolean>('helpText.home.headerPrayer');
  const { settingValue: readHT, isLoading: readHTLoading } = useUserSetting<boolean>('helpText.home.headerRead');
  const { settingValue: doHT, isLoading: doHTLoading } = useUserSetting<boolean>('helpText.home.headerDo');
  const { settingValue: plansHT, isLoading: plansHTLoading } = useUserSetting<boolean>('helpText.home.headerPlans');
  const { settingValue: settingsHT, isLoading: settingHTLoading } = useUserSetting<boolean>('helpText.home.settings');

  const htShowing = homeHT || prayerHT || readHT || doHT || plansHT || settingsHT;

  const isAdmin = user.isAdmin;

  if (homeHTLoading || prayerHTLoading || readHTLoading || doHTLoading || plansHTLoading || settingHTLoading)
    return null;

  return (
    <Navbar
      variant="dark"
      bg="primary"
      sticky="top"
      className="p-0"
      style={{ width: '100%' }}
      expand={htShowing ? 'md' : undefined}
      collapseOnSelect
    >
      {htShowing && (
        <Nav className={clsx({ 'd-md-none': htShowing, 'd-none': !htShowing })}>
          <Nav.Item className="mx-1">
            <Nav.Link as={NavLink} eventKey={1} to="/">
              <SHXIcon type="logo" size={30} />
              <ShxHelpText pageName="home" textName="headerHome" inline />
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}
      <Navbar.Toggle aria-controls="soulhx-header-navbar" />
      <Navbar.Collapse id="soulhx-header-navbar">
        <Nav>
          <Nav.Item className={htShowing ? 'd-none d-md-inline' : undefined}>
            <Nav.Link as={NavLink} eventKey={1} to="/" aria-label={t('header.linkNames.home')}>
              <SHXIcon type="logo" className="fs-1" />
            </Nav.Link>
            <ShxHelpText pageName="home" textName="headerHome" inline />
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} eventKey={2} to="/pray" aria-label={t('header.linkNames.prayer')}>
              <SHXIcon type="pray" className="fs-1" />
            </Nav.Link>
            <ShxHelpText pageName="home" textName="headerPrayer" inline />
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} eventKey={3} to="/read" aria-label={t('header.linkNames.read')}>
              <SHXIcon type="journal" className="fs-1" />
            </Nav.Link>
            <ShxHelpText pageName="home" textName="headerRead" inline />
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} eventKey={4} to="/do" aria-label={t('header.linkNames.do')}>
              <SHXIcon type="do" className="fs-1" />
            </Nav.Link>
            <ShxHelpText pageName="home" textName="headerDo" inline />
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} eventKey={5} to="/plans" aria-label={t('header.linkNames.plan')}>
              <SHXIcon type="readinglist" className="fs-1" />
            </Nav.Link>
            <ShxHelpText pageName="home" textName="headerPlans" inline />
          </Nav.Item>
          <HeaderCommsLink eventKey={6} />
          {isAdmin && (
            <Nav.Item>
              <Nav.Link as={NavLink} eventKey={7} to="/admin/users" aria-label={t('header.linkNames.admin.users')}>
                <SHXIcon type="admin" className="fs-1" />
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </Navbar.Collapse>
      <Navbar.Text className="ms-auto me-1">
        <ShxHelpText pageName="home" textName="settings" collapsed inline />

        <SHXButton
          variant="primary"
          className="text-white p-0"
          onClick={() => {
            setShowSettings((prev) => !prev);
          }}
          aria-label={t('header.settings')}
        >
          <Gear className="fs-1" />
        </SHXButton>
      </Navbar.Text>

      <Offcanvas
        show={showSettings}
        onHide={() => setShowSettings(false)}
        placement="end"
        aria-labelledby="settingspaneTitle"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="settingspaneTitle">{t('header.settings')}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Settings closeFunc={() => setShowSettings(false)} />
        </Offcanvas.Body>
      </Offcanvas>
    </Navbar>
  );
};

export default Header;
